import { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Trash, UserPlus, Download } from 'lucide-react'

import { CreateClientModal } from './CreateClientModal'
import Loader from '@/common/components/Loader'
import { IColumn, Table } from '@/core/components/Table'
import { formatDateTime } from '@/core/constants/times'
import { useTableParams } from '@/core/hooks/table-params'
import { useCheckPermission } from '@/core/hooks/use-check-permission'
import { type IClient } from '@/core/services/clients'
import { useAppSelector } from '@/store'
import { DeleteConfirmationModal } from '../DeleteConfirmationModal'
import { MessageBox } from '../messages/MessageBox'
import { Pagination } from '../Pagination'
import { useClients } from '@/home/hooks/use-clients'
import { Button, Card, Checkbox } from '@/components/ui'
import { Header, PageTitle } from '@/components'
import { Icon } from '@/common/components/Icon'

export type TableSearchParams = Record<string, string | null | undefined>

export const ClientsPage: React.FC = (): JSX.Element => {
  const {
    page,
    setPage,
    size,
    setSize,
    totalElements,
    setTotalElemenets,
    sortBy,
    setSortBy,
  } = useTableParams({
    extraParams: {
      email: '',
      phone: '',
    },
  })

  const { hasEditPermission } = useCheckPermission({ page: 'CLIENTS' })
  const { hasEditPermission: hasExportPermission } = useCheckPermission({
    page: 'CLIENTS_EXPORT',
  })

  const { festivalName, editionId } = useParams()
  const navigate = useNavigate()
  const [data, setData] = useState<IClient[]>([])
  const [isCheckAll, setIsCheckAll] = useState(false)
  const [isCheck, setIsCheck] = useState<string[]>([])
  const [showDeleteConfirmationModal, setDeleteConfirmationModal] =
    useState<boolean>(false)

  const [showCreateClientModal, setShowCreateClientModal] =
    useState<boolean>(false)
  const timer = useRef<NodeJS.Timeout | null>(null)

  const [isError, setIsError] = useState<boolean>(false)
  const [showMessageBox, setShowMessageBox] = useState<boolean>(false)
  const [message, setMessage] = useState<string>('')
  const [sortDirection, setSortDirection] = useState<
    'ASC' | 'DESC' | undefined
  >(undefined)

  const searchParams = useRef<TableSearchParams>({})
  const { fetchClients, loading } = useClients()

  const getData = useCallback(async () => {
    try {
      const params = {
        page,
        size,
        sortBy,
        sortDirection,
        email: searchParams.current.email,
        phone: searchParams.current.phoneNumber,
      }

      const res = await fetchClients(params)
      setData(res.content)
      setTotalElemenets(res.totalElements)
    } catch (err) {
      console.log(err)
    }
  }, [page, sortBy, size, sortDirection, fetchClients, setTotalElemenets])

  useEffect(() => {
    getData()
  }, [getData])

  const handleDelete = () => {
    if (!hasEditPermission) {
      return
    }
    if (isCheck.length > 0) {
      setDeleteConfirmationModal(true)
    }
  }

  const handleSelectAll = () => {
    if (!hasEditPermission) {
      return
    }
    setIsCheckAll(!isCheckAll)
    setIsCheck(data.map((clt) => clt.id))
    if (isCheckAll) {
      setIsCheck([])
    }
  }

  const handleClick = (id: string, checked: boolean) => {
    if (!hasEditPermission) {
      return
    }
    setIsCheck([...isCheck, id])
    if (!checked) {
      setIsCheck(isCheck.filter((item) => item !== id))
    }
  }

  const handleSortBy = (sort: string) => {
    if (sort === sortBy) {
      if (sortDirection === 'DESC') {
        setSortDirection('ASC')
      } else {
        setSortDirection(undefined)
        setSortBy(undefined)
      }
    } else {
      setSortDirection('DESC')
      setSortBy(sort)
    }
  }

  const deleteFunc = () => {
    ;(async () => {
      if (!hasEditPermission) {
        return
      }
      try {
        console.log('deleted')
        // TODO: Implement delete functionality
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
        } else {
          console.error(e)
        }
      }
    })()
  }

  const changePage = async (newPage: number) => {
    setPage(newPage)
  }

  const handleSearchParams = useCallback(
    async (col: IColumn, q: string | null) => {
      if (timer.current) {
        clearTimeout(timer.current)
      }

      searchParams.current = {
        ...searchParams.current,
        [col.accessor]: q,
      }

      const newTimer = setTimeout(async () => {
        try {
          const params = {
            page: 0,
            size,
            sortBy,
            email: searchParams.current.email,
            phone: searchParams.current.phoneNumber,
          }

          const res = await fetchClients(params)
          setData(res.content)
          setTotalElemenets(res.totalElements)
        } catch (err) {
          console.log(err)
        }
      }, 2000)
      timer.current = newTimer
    },
    [size, sortBy, fetchClients, setTotalElemenets],
  )

  const columns: IColumn[] = [
    {
      title: (
        <Checkbox
          disabled={!hasEditPermission}
          onCheckedChange={handleSelectAll}
          checked={isCheckAll}
        />
      ),
      accessor: '',
      render: (row) => (
        <Checkbox
          disabled={!hasEditPermission}
          onCheckedChange={(checked: boolean) => handleClick(row.id, checked)}
          checked={isCheck.includes(row.id)}
        />
      ),
    },
    {
      title: 'Id',
      accessor: 'id',
      sortable: true,
    },
    {
      title: 'Name',
      accessor: 'name',
      sortable: true,
    },
    {
      title: 'Phone Number',
      searchable: true,
      accessor: 'phoneNumber',
      sortable: true,
    },
    {
      title: 'Email',
      searchable: true,
      accessor: 'email',
      sortable: true,
    },
    {
      title: 'Details',
      accessor: '',
      render: (row) => (
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <Checkbox disabled checked={row.details.validatedEmail} />
            <span className="text-sm text-monochrome-2">Validated Email</span>
          </div>
          <div className="flex items-center gap-2">
            <Checkbox disabled checked={row.details.billingAddress} />
            <span className="text-sm text-monochrome-2">Billing Address</span>
          </div>
          <div className="flex items-center gap-2">
            <Checkbox disabled checked={row.details.shippingAddress} />
            <span className="text-sm text-monochrome-2">Shipping Address</span>
          </div>
        </div>
      ),
    },
    {
      title: 'Referral Code',
      accessor: 'referralCode',
    },
    {
      title: 'RUNA',
      accessor: 'runa',
    },
    {
      title: 'Last Activity',
      accessor: 'lastActivity',
      render: (row) => formatDateTime(row.lastActivity),
    },
    {
      title: 'Orders',
      accessor: 'ordersSize',
    },
    {
      title: 'View',
      accessor: '',
      render: (row) => (
        <a
          className="text-accent-1 underline"
          href="#"
          onClick={(e) => {
            e.preventDefault()
            navigate(`/clients/${festivalName}/${editionId}/${row.id}`)
          }}
        >
          View
        </a>
      ),
    },
  ]

  return (
    <>
      <Header
        pageTitle="Clients"
        actions={
          <>
            {hasExportPermission && (
              <Button
                variant="secondary"
                className="border border-monochrome-120"
                onClick={() => {
                  console.log('exported')
                }}
              >
                <Download className="w-4 h-4" />
                Export CSV
              </Button>
            )}
            {hasEditPermission && isCheck.length >= 1 && (
              <Button variant="destructive" onClick={handleDelete}>
                Delete
                <Icon
                  icon="close-circle"
                  width="18"
                  height="18"
                  viewBox="0 0 18 18"
                  fill="none"
                />
              </Button>
            )}
            {hasEditPermission && (
              <Button
                variant="primary"
                onClick={() => {
                  setShowCreateClientModal(true)
                }}
              >
                Add New
                <UserPlus className="w-4 h-4 shrink-0" />
              </Button>
            )}
          </>
        }
      />
      {loading && <Loader />}
      <Card className="p-5 !bg-monochrome-4-60">
        <PageTitle title="Clients" className="mb-5" />
        <Table
          isCheck={isCheck}
          columns={columns}
          handleSortBy={handleSortBy}
          data={data}
          handleSearch={(col, query) => {
            handleSearchParams(col, query)
          }}
          loading={loading}
          noDataMessage={
            searchParams.current.email ? (
              <span>
                Sorry, <b>Email</b> not found
              </span>
            ) : searchParams.current.phoneNumber ? (
              <span>
                Sorry, <b>Phone</b> not found
              </span>
            ) : (
              <span>Sorry, There are no data</span>
            )
          }
        />
      </Card>
      <Pagination
        size={size}
        totalElements={totalElements}
        setSize={setSize}
        page={page + 1}
        changePage={changePage}
      />
      <CreateClientModal
        showCreateModal={showCreateClientModal}
        setShowCreateModal={setShowCreateClientModal}
        page={page}
        size={size}
        sortBy={sortBy}
        setCLients={setData}
        setTotalElemenets={setTotalElemenets}
      />
      <DeleteConfirmationModal
        showDeleteConfirmationModal={showDeleteConfirmationModal}
        setDeleteConfirmationModal={setDeleteConfirmationModal}
        deleteTitle={'Client'}
        deleteFunc={deleteFunc}
      />
      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
      />
    </>
  )
}
